/**
 * 监测器模块（OFBiz 新接口）
 * ------------------------------------------------------------------
 * 对应后端：POST /itoss/control/json/monitor/*
 *
 * 迁移自前端原接口（odata_equipment_client.js / odataEquipment.js）：
 *   getMonitorData, getMonitorsPropertyDataFromID, addMonitorProperty,
 *   updateMonitorProperty, deleteMonitorProperty, setMonitorIsAlarm,
 *   refreshMonitor, getMonitorCount, getMonitors(类型字典),
 *   getMonitorReturnValues, getAlarmConditionDataFromMonitorId,
 *   getInitialAlarmConditionDataFromMonitorType
 *
 * 字段映射（旧 PascalCase -> 新 camelCase）：
 *   RecId            -> monitorId
 *   Title            -> monitorName / fixedAssetName
 *   MonitorType      -> monitorType / MONITOR_TYPE
 *   MonitorStatus    -> monitorStatus
 *   IsDisable        -> isDisabled
 *   SaveItem         -> returnItems
 *   (频率)           -> REFRESH_TIME + REFRESH_UNIT
 *   (阈值)           -> WARN_THRESHOLD / ERROR_THRESHOLD / THRESHOLD_OP
 */
import { ofbiz } from './http';
import { adaptMonitorTypeItemToLegacy } from './adapters';

/**
 * 资源监测树（组→设备→监测器 嵌套 + 全局状态统计）
 *
 * 对应前端 getDeviceMonitorTreeData。一次性返回完整树，替代旧的
 * EccGroup?$expand=GroupEquipment($expand=EquipmentMonitor)。
 *
 * @param {object} [opts]
 * @param {string} [opts.groupId] 可选，指定则只返回该分组子树
 * @returns {Promise<object>} data，含 tree / statusStatistics / 各 count
 */
export function getMonitorTree({ groupId } = {}) {
  return ofbiz.post('monitor/tree', null, { groupId });
}

/**
 * 监测器状态统计（替代旧 getMonitorCount / getStateStatisticsData）
 * @param {object} [opts]
 * @param {string} [opts.parentFixedAssetId] 可选，按设备统计
 * @returns {Promise<object>} 状态计数 { good, warning, error, disabled, ... }
 */
export function getMonitorStateStatistics({ parentFixedAssetId } = {}) {
  return ofbiz.post('monitor/stateStatistics', null, { parentFixedAssetId });
}

/**
 * 监测器类型字典（替代旧 getMonitors / EccMonitorList）
 * @param {object} [opts]
 * @param {string} [opts.equipType] 可选，按设备类型过滤适用监测器
 * @returns {Promise<Array>} 监测器类型列表
 */
export function getMonitorTypes({ equipType } = {}) {
  return ofbiz.post('monitor/types', null, { equipType }).then(data => {
    // 把 OFBiz 嵌套 attributes 结构扁平化成旧前端期望的 PascalCase 字段
    const list = (data && data.list) || (Array.isArray(data) ? data : []);
    const adapted = list.map(adaptMonitorTypeItemToLegacy);
    // 保持原始响应结构（list/total 等），只替换 list 里的每项
    if (Array.isArray(data)) return adapted;
    return { ...data, list: adapted };
  });
}

/**
 * 监测器返回项指标定义（替代旧 getMonitorReturnValues / Item）
 * @param {string} monitorType 监测器类型枚举 IT_MON_*
 * @returns {Promise<Array>}
 */
export function getMonitorReturnItems(monitorType) {
  return ofbiz.post('monitor/returnItems', null, { monitorType });
}

/**
 * 批量获取多种类型的指标项（后端 monitorTypes 批量参数，一次请求）
 * 行内 monitorType 回传原始类型短码（如 Ping/SnmpCpu），前端按短码分组
 * @param {string} monitorTypes 逗号分隔的类型短码
 * @returns {Promise<Array>}
 */
export function getMonitorReturnItemsBatch(monitorTypes) {
  return ofbiz.post('monitor/returnItems', null, { monitorTypes });
}

/**
 * 监测器告警条件模板（替代旧 getAlarmConditionDataFromMonitorId
 * / getInitialAlarmConditionDataFromMonitorType）
 * @param {string} monitorType 监测器类型枚举 IT_MON_*
 * @returns {Promise<Array>}
 */
export function getMonitorAlarmConditions(monitorType) {
  return ofbiz.post('monitor/alarmConditions', null, { monitorType });
}

/**
 * 任务计划列表（替代旧 /bods.svc/EccTaskPlan）
 *
 * 后端内置默认两条（7*24 / 5*8，与老库 ecctaskplan 初始化数据一致），
 * 返回字段保持旧 PascalCase 契约：RecId / TaskName / Instruction / Model / Status。
 * Status 格式 "星期@小时列表:..."，星期 1=周日、2=周一...7=周六。
 * @returns {Promise<Array>}
 */
export function getTaskPlanList() {
  return ofbiz.post('monitor/taskplan/list').then(data => {
    return (data && data.list) || (Array.isArray(data) ? data : []);
  });
}

/**
 * 监测器列表（分页，替代旧 getMonitorData）
 * @param {object} [query] { parentFixedAssetId, fixedAssetName, monitorType, status }
 * @param {number} [page=1]
 * @param {number} [pageSize=20]
 * @returns {Promise<{list, total, page, pageSize}>}
 */
export function getMonitorList(query = {}, page = 1, pageSize = 20) {
  return ofbiz.postPage('monitor/list', query, page, pageSize);
}

/**
 * 监测器详情（替代旧 getMonitorsPropertyDataFromID）
 * @param {string} monitorId
 * @returns {Promise<object>}
 */
export function getMonitorById(monitorId) {
  return ofbiz.post('monitor/getById', null, { monitorId });
}

/**
 * 新增监测器（替代旧 addMonitorProperty）
 * @param {object} data 见 OpenAPI：parentFixedAssetId, MONITOR_TYPE, fixedAssetName,
 *                      COLLECT_PROTOCOL, TARGET_HOST/PORT, REFRESH_TIME/UNIT, TIMEOUT,
 *                      SPECIAL_PROPS, WARN/ERROR_THRESHOLD, THRESHOLD_OP
 * @returns {Promise<object>}
 */
export function createMonitor(data) {
  return ofbiz.post('monitor/create', data);
}

/**
 * 更新监测器（替代旧 updateMonitorProperty / setMonitorIsAlarm / setMonitorRefresh）
 *
 * 启用/禁用、刷新频率等也都走 update：
 *   - setMonitorIsAlarm -> update 传 IS_DISABLED
 *   - setMonitorRefresh -> update 传 REFRESH_TIME / REFRESH_UNIT
 * @param {object} data 必含 monitorId
 * @returns {Promise<object>}
 */
export function updateMonitor(data) {
  // monitor/update 成功响应无 data 字段（仅 code/success/message），
  // http 层会 resolve(undefined)，这里包一层保证调用方 if(result) 判定成功
  return ofbiz.post('monitor/update', data).then(
    (data2) => data2 !== undefined ? data2 : {},
    (err) => Promise.reject(err),
  );
}

/**
 * 删除监测器（替代旧 deleteMonitorProperty）
 * @param {string} monitorId
 * @returns {Promise<object>}
 */
export function deleteMonitor(monitorId) {
  return ofbiz.post('monitor/delete', null, { monitorId });
}

/**
 * 启用监测器（替代旧 setMonitorIsAlarm 中"启用"分支）
 * @param {string} monitorId
 */
export function enableMonitor(monitorId) {
  return ofbiz.post('monitor/enable', null, { monitorId });
}

/**
 * 禁用监测器
 * @param {string} monitorId
 */
export function disableMonitor(monitorId) {
  return ofbiz.post('monitor/disable', null, { monitorId });
}

/**
 * 手动刷新监测器（立即采集，替代旧 refreshMonitor 单个）
 * @param {string} monitorId
 */
export function refreshMonitor(monitorId) {
  return ofbiz.post('monitor/refresh', null, { monitorId });
}

/**
 * 批量刷新监测器（异步，替代旧 refreshMonitor 批量）
 * @param {string} monitorIds 逗号分隔的监测器 ID
 */
export function refreshMonitorBatch(monitorIds) {
  return ofbiz.post('monitor/refreshBatch', null, { monitorIds });
}
